import React, { Fragment, useState, useEffect } from 'react';
import { Col, Card, CardHeader, CardBody, Form, Label, Row, Button } from 'reactstrap';
import { H3, Breadcrumbs } from '../../AbstractElements';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Typeahead from '../../CommonElements/TypeAhead';
import moment from 'moment';
import { getClientUrlDataToken, postClientUrlWithToken } from '../../Utils/restUtils';
import { getLeaveList, getLeaveListRole, viewAgent, viewAgentRole, storeLeaveRequest, storeLeaveRequestRole, getUsers, getuserRole, getUsersRole } from '../../CommonUrl';
import Transparentspinner from '../Common/Loader/Transparentspinner';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

const leaveSchema = yup.object().shape({
    staff: yup.mixed().required("Staff is required"),
    leaveType: yup.mixed().required("Leave type is required"),
    fromDate: yup.date().required("From date is required").typeError("Invalid from date"),
    toDate: yup.date()
        .required("To date is required")
        .typeError("Invalid to date")
        .min(yup.ref('fromDate'), "To date cannot be before From date"),
    reason: yup.string().required("Reason is required").min(5, "Reason must be at least 5 characters").max(200, "Reason cannot exceed 200 characters")
});


function AddEditLeave() {
    const navigate = useNavigate();
    const authState = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [leaveTypeList, setLeaveTypeList] = useState([]);

    const { register, handleSubmit, control, formState: { errors } } = useForm({
        resolver: yupResolver(leaveSchema),
        defaultValues: {
            staff: null,
            leaveType: null,
            fromDate: null,
            toDate: null,
            reason: ''
        }
    });

    const fetchStaff = async () => {
        setLoading(true);
        try {
            const endpoint = authState.userRole === 1 ? getUsers : getUsersRole;
            const response = await getClientUrlDataToken(
                authState.apiUrl, 
                `${endpoint}`
            );
            if (response.status === true) {
                const staffOptions = response?.data?.map(e => ({
                    value: e?.user_id,
                    label: e?.user_name                }));
                setStaffList(staffOptions);
            } else {
                toast.error("Failed to load staff list");
            }
        } catch (err) {
            console.error("Error loading staff list:", err);
            toast.error("An error occurred while loading staff");
        } finally {
            setLoading(false);
        }
    };
    const fetchLeaveTypes = async () => {
        setLoading(true);
        try {
            const endpoint = authState.userRole === 1 ? getLeaveList : getLeaveListRole;
            const response = await getClientUrlDataToken(
                authState.apiUrl, 
                `${endpoint}`
            );
            if (response.status === true ) {
                const leaveTypeOptions = response.data.map(e => ({
                    value: String(e.id),
                    label: e.name || `Leave Type #${e.id}`
                }));
                setLeaveTypeList(leaveTypeOptions);
            } else {
                toast.error("Failed to load leave types");
            }
        } catch (err) {
            console.error("Error loading leave types:", err);
            toast.error("An error occurred while loading leave types");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStaff();
        fetchLeaveTypes();
    }, []);

    const onSubmit = async (data) => {
        setLoading(true);
        const postRequest = {
            leave_type_id: data.leaveType?.value,
            reason: data.reason,
            from_date: data.fromDate ? moment(data.fromDate).format("DD-MM-YYYY") : null,
            to_date: data.toDate ? moment(data.toDate).format("DD-MM-YYYY") : null,
            employee_id: data.staff?.value
        };

        const endpoint = authState.userRole === 1 ? storeLeaveRequest : storeLeaveRequestRole;

        try {
            const response = await postClientUrlWithToken(
                authState.apiUrl,
                endpoint,
                postRequest
            );

            if (response.status === 200) {
                if (response.data.status === true) {
                    toast.success(response.data.message || "Leave applied successfully!");
                    navigate(`${process.env.PUBLIC_URL}/attendance/leave-management`);
                } else {
                    if (Array.isArray(response.data.message)) {
                        response.data.message.forEach((err) => {
                            toast.error(err);
                        });
                    } else {
                        toast.error(response.data.message || "An error occurred");
                    }
                }
            } else {
                toast.error("Failed to apply leave. Please check your inputs.");
            }
        } catch (err) {
            console.error("Error applying leave:", err);
            toast.error("An error occurred while applying leave");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(`${process.env.PUBLIC_URL}/attendance/leave-management`);
    };

    return (
        <Fragment>
            {loading && <Transparentspinner />}
            <Breadcrumbs mainTitle="Add Leave" parent="Attendance" title="Add Leave" />
            <div className="container-fluid">
                <Card className="shadow-sm" style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                    <CardHeader className="pb-0" style={{ borderBottom: 'none' }}>
                        <H3 className="mb-0">Add Leave</H3>
                    </CardHeader>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit)} className="needs-validation" noValidate>
                            <Row className="g-3">
                                <Col md="6">
                                    <Label className="form-label" >
                                        Staff <span className="text-danger">*</span>
                                    </Label>
                                    <Controller
                                        name="staff"
                                        control={control}
                                        render={({ field }) => (
                                            <Typeahead
                                                options={staffList}
                                                onChange={(value) => field.onChange(value)}
                                                value={field.value}
                                                placeholder="Select Staff Employee"
                                            />
                                        )}
                                    />
                                    {errors.staff && <p className="text-danger mt-1">{errors.staff.message}</p>}
                                </Col>

                                <Col md="6">
                                    <Label className="form-label" >
                                        Leave Type <span className="text-danger">*</span>
                                    </Label>
                                    <Controller
                                        name="leaveType"
                                        control={control}
                                        render={({ field }) => (
                                            <Typeahead
                                                options={leaveTypeList}
                                                onChange={(value) => field.onChange(value)}
                                                value={field.value}
                                                placeholder="Select Leave Type"
                                            />
                                        )}
                                    />
                                    {errors.leaveType && <p className="text-danger mt-1">{errors.leaveType.message}</p>}
                                </Col>

                                <Col md="6">
                                    <Label className="form-label" >
                                        From Date <span className="text-danger">*</span>
                                    </Label>
                                    <Controller
                                        name="fromDate"
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                                placeholderText="dd/mm/yyyy"
                                                dateFormat="dd/MM/yyyy"
                                                autoComplete="off"
                                                selected={field.value}
                                                onChange={(date) => field.onChange(date)}
                                                className="form-control"
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="select"
                                            />
                                        )}
                                    />
                                    {errors.fromDate && <p className="text-danger mt-1">{errors.fromDate.message}</p>}
                                </Col>

                                <Col md="6">
                                    <Label className="form-label" >
                                        To Date <span className="text-danger">*</span>
                                    </Label>
                                    <Controller
                                        name="toDate"
                                        control={control}
                                        render={({ field }) => (
                                            <DatePicker
                                                placeholderText="dd/mm/yyyy"
                                                dateFormat="dd/MM/yyyy"
                                                autoComplete="off"
                                                selected={field.value}
                                                onChange={(date) => field.onChange(date)}
                                                className="form-control"
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="select"
                                            />
                                        )}
                                    />
                                    {errors.toDate && <p className="text-danger mt-1">{errors.toDate.message}</p>}
                                </Col>

                                <Col md="12">
                                    <Label className="form-label" >
                                        Reason <span className="text-danger">*</span>
                                    </Label>
                                    <textarea
                                        className="form-control"
                                        rows="4"
                                        placeholder="State the reason for leave..."
                                        {...register("reason")}
                                        style={{ borderRadius: '6px' }}
                                    />
                                    {errors.reason && <p className="text-danger mt-1">{errors.reason.message}</p>}
                                </Col>
                            </Row>

                            <div className="text-end mt-4">
                                <Button color="light" type="button" className="me-2" onClick={handleCancel} style={{ borderRadius: '6px' }}>
                                    Cancel
                                </Button>
                                <Button color="primary" type="submit" style={{ borderRadius: '6px' }}>
                                    Add Leave
                                </Button>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </div>
        </Fragment>
    );
}

export default AddEditLeave;